import { Camera, CircleAlert, FileUp, Images } from "lucide-react";
import { type DragEvent, useRef, useState } from "react";
import { IMPORT_ACCEPT } from "../lib/importFiles";
import type { PrepSource } from "../lib/prepare/usePagePreparation";
import { ICON } from "./ui";

/**
 * Hidden file inputs for the import: the phone camera (one photo of a menu page at a time), the phone's photos
 * (several at once) and files (PDFs or images). `open` shows the picker; the chosen files go to `onFiles` with
 * where they came from.
 */
export function useDocumentPicker(onFiles: (files: File[], source: PrepSource) => void) {
  const refs = {
    camera: useRef<HTMLInputElement>(null),
    gallery: useRef<HTMLInputElement>(null),
    files: useRef<HTMLInputElement>(null),
  };
  const pick = (source: PrepSource) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = [...(e.target.files ?? [])];
    e.target.value = "";
    if (list.length) onFiles(list, source);
  };
  const hidden = { tabIndex: -1, "aria-hidden": true, style: { display: "none" } } as const;
  const inputs = (
    <>
      {/* capture: the phone opens its camera; on a computer it is an ordinary image picker. */}
      <input
        ref={refs.camera}
        type="file"
        accept="image/*"
        capture="environment"
        data-picker="camera"
        {...hidden}
        onChange={pick("camera")}
      />
      <input
        ref={refs.gallery}
        type="file"
        multiple
        accept="image/*"
        data-picker="gallery"
        {...hidden}
        onChange={pick("gallery")}
      />
      <input
        ref={refs.files}
        type="file"
        multiple
        accept={IMPORT_ACCEPT}
        data-picker="files"
        {...hidden}
        onChange={pick("files")}
      />
    </>
  );
  return { inputs, open: (source: PrepSource) => refs[source].current?.click() };
}

/**
 * Prototype import drop zone, the first choice of how the menu comes in. On a phone: photograph the pages now
 * (camera), choose photos already taken, or a PDF file; on a computer: drag & drop or pick files. Once the first
 * page is in, the page preview takes its place and offers more of the same (owner, 2026-10-22).
 */
export function UploadZone({
  busy,
  error,
  onFiles,
  busyLabel = "Se încarcă…",
}: {
  busy: boolean;
  error: string;
  onFiles: (files: File[], source: PrepSource) => void;
  busyLabel?: string;
}) {
  const picker = useDocumentPicker(onFiles);
  const [drag, setDrag] = useState(false);
  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDrag(false);
    if (!busy) onFiles([...e.dataTransfer.files], "files");
  };
  return (
    <section
      className={`dropzone${drag ? " drag" : ""}`}
      aria-label="Încarcă meniul"
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
    >
      <span className="dropzone-icon" aria-hidden="true">
        <span className="desktop-only">
          <FileUp {...ICON} />
        </span>
        <span className="mobile-only">
          <Camera {...ICON} />
        </span>
      </span>
      <span className="h2">
        <span className="desktop-only">Trage meniul aici sau alege fișierul</span>
        <span className="mobile-only">Cum adaugi meniul?</span>
      </span>
      <span className="muted" style={{ fontSize: 15, lineHeight: "22px", maxWidth: 480 }}>
        <span className="desktop-only">
          PDF, JPG, PNG sau WebP, până la 20 MB. Documentele cu mai multe pagini sunt acceptate. Pe telefon
          poți fotografia meniul.
        </span>
        <span className="mobile-only">
          Fotografiezi paginile acum, una după alta, alegi fotografiile făcute deja sau încarci un PDF.
        </span>
      </span>
      {picker.inputs}
      <span className="mobile-only" style={{ width: "100%", maxWidth: 340 }}>
        <span className="stack" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={busy}
            onClick={() => picker.open("camera")}
          >
            {busy ? (
              busyLabel
            ) : (
              <>
                <Camera {...ICON} aria-hidden="true" />
                Fotografiază paginile acum
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            disabled={busy}
            onClick={() => picker.open("gallery")}
          >
            <Images {...ICON} aria-hidden="true" />
            Alege fotografii din telefon
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            disabled={busy}
            onClick={() => picker.open("files")}
          >
            <FileUp {...ICON} aria-hidden="true" />
            Încarcă un fișier PDF
          </button>
        </span>
      </span>
      <span className="desktop-only">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={() => picker.open("files")}
        >
          {busy ? busyLabel : "Alege fișierul"}
        </button>
      </span>
      {error && (
        <span role="alert" className="field-error">
          <CircleAlert size={16} strokeWidth={1.75} aria-hidden="true" />
          <span>{error}</span>
        </span>
      )}
    </section>
  );
}
